import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Copy, DollarSign, Users, TrendingUp, Clock, CheckCircle, Info } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AffiliateDashboard() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/affiliates/dashboard`, {
        credentials: 'include'
      });

      if (res.status === 404) {
        setDashboardData(null);
      } else if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching affiliate dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the Affiliate Program Terms');
      return;
    }

    setRegistering(true);
    try {
      const res = await fetch(`${API}/affiliates/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agree_to_terms: true })
      });

      if (!res.ok) {
        throw new Error('Registration failed');
      }

      toast.success('Affiliate registration submitted! Awaiting admin approval.');
      await fetchDashboard();
    } catch (error) {
      toast.error('Failed to register as affiliate');
    } finally {
      setRegistering(false);
    }
  };

  const copyReferralLink = () => {
    if (dashboardData?.referral_url) {
      navigator.clipboard.writeText(dashboardData.referral_url);
      toast.success('Referral link copied!');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
      </div>
    );
  }

  // Not registered yet
  if (!dashboardData) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
            <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
            <span>/</span>
            <span className="text-white">Affiliates</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Affiliate Program</h1>
          <p className="text-[#8A93A6]">Earn 30% recurring commissions</p>
        </div>

        <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400 text-sm">
            <strong>Note:</strong> Affiliate program settings are managed in Admin.
          </AlertDescription>
        </Alert>

        <Card className="glass">
          <CardHeader>
            <CardTitle>Join the Consistency Affiliate Program</CardTitle>
            <CardDescription>Earn recurring revenue by referring customers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <DollarSign className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-white">30% Commission</p>
                <p className="text-sm text-[#8A93A6]">On all paid plans</p>
              </div>
              <div className="text-center">
                <TrendingUp className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="font-semibold text-white">12-Month Cap</p>
                <p className="text-sm text-[#8A93A6]">Per referred user</p>
              </div>
              <div className="text-center">
                <Users className="h-8 w-8 text-pink-500 mx-auto mb-2" />
                <p className="font-semibold text-white">Monthly Payouts</p>
                <p className="text-sm text-[#8A93A6]">Via Stripe Connect</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <h3 className="font-semibold text-white mb-3">How it Works</h3>
              <ol className="space-y-2 text-sm text-[#8A93A6]">
                <li>1. Get approved as an affiliate</li>
                <li>2. Share your unique referral link</li>
                <li>3. Earn 30% commission on net revenue for 12 months</li>
                <li>4. Receive monthly payouts via Stripe</li>
              </ol>
            </div>

            {/* Payout Rule - LOCKED V4 TEXT */}
            <div className="border-t border-white/10 pt-6">
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <Info className="h-4 w-4 text-yellow-500" />
                <AlertDescription className="text-yellow-400 text-xs">
                  <strong>Payout Policy:</strong> Affiliate payouts are issued monthly, with a 30-day verification period after payment is received. 
                  Trials, Free Pro months, and promotional pricing remain pending until cleared revenue exists.
                </AlertDescription>
              </Alert>
            </div>

            <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <Checkbox
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                id="terms"
              />
              <label htmlFor="terms" className="text-sm text-[#8A93A6] cursor-pointer">
                I agree to the{' '}
                <a href="/legal/affiliate-terms" target="_blank" className="text-blue-400 hover:underline">
                  Affiliate Program Terms
                </a>
              </label>
            </div>

            <Button
              onClick={handleRegister}
              disabled={registering || !agreedToTerms}
              className="w-full"
            >
              {registering ? 'Submitting...' : 'Apply to Join'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registered affiliate dashboard
  const { affiliate, stats, commissions } = dashboardData;

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
          <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
          <span>/</span>
          <span className="text-white">Affiliates</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Affiliate Dashboard</h1>
        <div className="flex items-center gap-2">
          <Badge variant={affiliate.status === 'approved' ? 'default' : 'outline'}>
            {affiliate.status}
          </Badge>
          {affiliate.status === 'pending' && (
            <p className="text-sm text-[#8A93A6]">Awaiting admin approval</p>
          )}
        </div>
      </div>

      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Note:</strong> Affiliate program settings are managed in Admin.
        </AlertDescription>
      </Alert>

      {/* Payout Rule Alert - LOCKED V4 TEXT */}
      <Alert className="bg-yellow-500/10 border-yellow-500/30 mb-6">
        <Info className="h-4 w-4 text-yellow-500" />
        <AlertDescription className="text-yellow-400 text-sm">
          <strong>Payout Policy:</strong> Affiliate payouts are issued monthly, with a 30-day verification period after payment is received. 
          Trials, Free Pro months, and promotional pricing remain pending until cleared revenue exists.
        </AlertDescription>
      </Alert>

      {/* Stats - Pending vs Payable Split */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="glass">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#8A93A6]">Total Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-white">{stats.total_referrals}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#8A93A6]">Paying Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-white">{stats.paying_referrals}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-green-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#8A93A6]">Payable Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-1">
              <DollarSign className="h-4 w-4 text-green-500 opacity-50 mt-1" />
              <span className="text-2xl font-bold text-green-400">
                {(stats.total_earnings - stats.pending_earnings).toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[#8A93A6] mt-1">Cleared revenue</p>
          </CardContent>
        </Card>

        <Card className="glass border-yellow-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#8A93A6]">Pending Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-1">
              <Clock className="h-4 w-4 text-yellow-500 opacity-50 mt-1" />
              <span className="text-2xl font-bold text-yellow-400">
                {stats.pending_earnings.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[#8A93A6] mt-1">Awaiting verification</p>
          </CardContent>
        </Card>
      </div>

      {/* Stripe Connect Setup */}
      {affiliate.status === 'approved' && !dashboardData.connect_status?.connected && (
        <Card className="glass mb-6 border-blue-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-500" />
              Set Up Payout Account
            </CardTitle>
            <CardDescription>
              Connect your bank account via Stripe to receive affiliate payouts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="mb-3">Stripe Connect Onboarding</Badge>
            <Button 
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/stripe/connect/create-account-link`, {
                    method: 'POST',
                    credentials: 'include'
                  });
                  const data = await res.json();
                  if (data.url) {
                    window.location.href = data.url;
                  }
                } catch (error) {
                  toast.error('Failed to create account link');
                }
              }}
              className="w-full sm:w-auto"
            >
              Connect Bank Account
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Referral Link */}
      {affiliate.status === 'approved' && (
        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle>Your Referral Link</CardTitle>
            <CardDescription>
              Share this link to earn 30% commission on all subscriptions for 12 months
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={dashboardData.referral_url}
                readOnly
                className="flex-1"
              />
              <Button onClick={copyReferralLink} variant="outline">
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            {dashboardData.connect_status?.connected && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-500">
                <CheckCircle className="h-4 w-4" />
                <span>Payout account connected</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commission History */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Commission History</CardTitle>
          <CardDescription>Your earnings over time</CardDescription>
        </CardHeader>
        <CardContent>
          {commissions && commissions.length > 0 ? (
            <div className="space-y-2">
              {commissions.map((commission) => (
                <div
                  key={commission.id}
                  className="flex items-center justify-between p-3 glass rounded-lg"
                >
                  <div>
                    <p className="text-white font-medium">${commission.amount.toFixed(2)}</p>
                    <p className="text-xs text-[#8A93A6]">{new Date(commission.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge variant={commission.status === 'paid' ? 'default' : 'outline'}>
                    {commission.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-[#8A93A6] mx-auto mb-3" />
              <p className="text-[#8A93A6]">No commissions yet</p>
              <p className="text-sm text-[#8A93A6] mt-1">Share your referral link to start earning</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
